"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ForwardRefExoticComponent, RefAttributes, SVGProps } from "react";

import { BetaPill } from "@/app/components/BetaPill";
import { TTV_EDITOR_PATH } from "@/features/ttv/navigation/constants";
import { TtvNavigationLink } from "@/features/ttv/navigation/TtvNavigationLink";
import { cn } from "@/utils/cn";

export function MenuItem({
  navItem,
  isNew,
  isBeta,
  clickHandler,
}: {
  navItem: {
    name: string;
    href: string;
    icon: ForwardRefExoticComponent<
      Omit<SVGProps<SVGSVGElement>, "ref"> & {
        title?: string | undefined;
        titleId?: string | undefined;
      } & RefAttributes<SVGSVGElement>
    >;
  };
  isNew: boolean;
  isBeta: boolean;
  clickHandler: (() => void) | null;
}) {
  const pathname = usePathname();

  const current = pathname.startsWith(navItem.href);
  const linkClassName = cn(
    current ?
      "bg-gray-50 text-primary"
    : "text-gray-900 hover:bg-gray-50 hover:text-primary",
    "group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 transition-colors"
  );
  const linkContent = (
    <>
      <navItem.icon
        className={cn(
          current ? "text-primary" : "text-gray-400 group-hover:text-primary",
          "h-6 w-6 shrink-0 transition-colors"
        )}
        aria-hidden="true"
      />
      {navItem.name}
      {isBeta && <BetaPill className="ml-auto self-center" />}
    </>
  );

  return (
    <li className="relative">
      {navItem.href === TTV_EDITOR_PATH ?
        <TtvNavigationLink
          href={navItem.href}
          className={linkClassName}
          onNavigationComplete={clickHandler ?? undefined}
        >
          {linkContent}
        </TtvNavigationLink>
      : <Link
          href={navItem.href}
          className={linkClassName}
          onClick={clickHandler ?? undefined}
        >
          {linkContent}
        </Link>
      }
      {isNew && (
        <div className="pointer-events-none absolute -top-1 right-4 z-10">
          <span className="rounded-md bg-gray-900/50 px-2 py-1 text-xs font-medium text-white">
            👀 New
          </span>
        </div>
      )}
    </li>
  );
}
